import { useEffect, useRef, useState } from 'react';
import { Space } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import FeatureGuidePanel from './FeatureGuidePanel';
import FeatureGuidePanelSection from './FeatureGuidePanelSection';
import { FeatureGuide } from './types';
import { StyledFeatureGuidePagination, StyledFeatureGuideSidebar } from './styles';

type Props = {
    guides: FeatureGuide[];
    activeStepKey?: string;
};

const FeatureGuideSidebar = ({ guides, activeStepKey }: Props) => {
    const [index, setIndex] = useState(0);

    // Read through a ref so a freshly built `guides` array doesn't retrigger the step sync.
    const guidesRef = useRef(guides);
    guidesRef.current = guides;

    // Following the wizard: a step change pulls the sidebar to that step's first guide.
    // Prev/Next then browses the whole list freely until the step changes again.
    useEffect(() => {
        if (!activeStepKey) return;
        const target = guidesRef.current.findIndex((guide) => guide.stepKey === activeStepKey);
        if (target !== -1) setIndex(target);
    }, [activeStepKey]);

    // The guide list depends on country/type, so it can shrink under a stale index.
    const currentIndex = Math.min(index, guides.length - 1);
    const guide = guides[currentIndex];

    if (!guide) return null;

    const footer =
        guides.length > 1 ? (
            <StyledFeatureGuidePagination>
                <Button type="link" disabled={currentIndex === 0} onClick={() => setIndex(currentIndex - 1)}>
                    <Space size={8}>
                        <LeftOutlined style={{ fontSize: 12 }} />
                        Prev
                    </Space>
                </Button>
                <Button
                    type="link"
                    disabled={currentIndex === guides.length - 1}
                    onClick={() => setIndex(currentIndex + 1)}
                >
                    <Space size={8}>
                        Next
                        <RightOutlined style={{ fontSize: 12 }} />
                    </Space>
                </Button>
            </StyledFeatureGuidePagination>
        ) : undefined;

    return (
        <StyledFeatureGuideSidebar>
            <FeatureGuidePanel title={guide.title} icon={guide.icon} footer={footer}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {guide.sections.map((section) => (
                        <FeatureGuidePanelSection
                            key={section.title}
                            title={section.title}
                            icon={section.icon}
                            content={section.content}
                        />
                    ))}
                </Space>
            </FeatureGuidePanel>
        </StyledFeatureGuideSidebar>
    );
};

export default FeatureGuideSidebar;
